import { FC, useState, useCallback } from 'react';
import { Button, Card, MonthPicker } from '@features/ui';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { startOfMonth, format } from 'date-fns';
import { getEvalAccountByMonth } from '@src/services/reports';
import { TReportEvalAccountByMonthRes } from '@src/types';
import { toast } from 'react-toastify';

type ChartDataPoint = {
  label: string;
  new_accounts: number;
  renewals: number;
};

type TooltipPayloadItem = {
  value: number;
  dataKey?: string;
  color?: string;
  payload?: ChartDataPoint;
};

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string;
}

const CustomTooltip: FC<CustomTooltipProps> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const newAccounts =
      payload.find((p) => p.dataKey === 'new_accounts')?.value ?? 0;
    const renewals = payload.find((p) => p.dataKey === 'renewals')?.value ?? 0;
    const total = newAccounts + renewals;

    return (
      <div
        className="bg-card-stat p-3 rounded-lg text-xs border-1 border-[#3D3D3D] flex flex-col min-w-[200px] gap-y-2"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-white font-semibold text-sm text-center mb-1">
          {label}
        </p>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-[#4A90E2]" />
            <span className="text-[#999999]">New Accounts:</span>
          </div>
          <span className="text-white font-semibold">{newAccounts}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-[#B6D72C]" />
            <span className="text-[#999999]">Renewals:</span>
          </div>
          <span className="text-white font-semibold">{renewals}</span>
        </div>
        <div className="border-t border-[#3D3D3D] pt-2 mt-1">
          <div className="flex justify-between">
            <span className="text-[#999999]">Total:</span>
            <span className="text-[#B6D72C] font-semibold">{total}</span>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

interface CustomLegendProps {
  title?: string;
}

const CustomLegend: FC<CustomLegendProps> = ({ title }) => {
  return (
    <div className="flex justify-between items-center mb-5">
      <p className="text-2xl">{title ?? 'Eval Account By Month'}</p>
      <div className="flex gap-x-6 p-2.5">
        <div className="flex items-center gap-x-1.5">
          <div className="w-9 h-3 bg-[#4A90E2]" />
          <p className="text-sm text-[#999999]">New Accounts</p>
        </div>
        <div className="flex items-center gap-x-1.5">
          <div className="w-9 h-3 bg-[#B6D72C]" />
          <p className="text-sm text-[#999999]">Renewals</p>
        </div>
      </div>
    </div>
  );
};

export const EvalAccountByMonth: FC = () => {
  const [selectedMonth, setSelectedMonth] = useState<Date | undefined>(
    startOfMonth(new Date())
  );
  const [useAllTime, setUseAllTime] = useState(false);
  const [reportData, setReportData] =
    useState<TReportEvalAccountByMonthRes | null>(null);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const buildParams = useCallback(() => {
    const month = useAllTime
      ? 'all'
      : selectedMonth
        ? format(selectedMonth, 'yyyy-MM')
        : 'all';

    return { month };
  }, [useAllTime, selectedMonth]);

  const handleGetData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await getEvalAccountByMonth(buildParams());

      setReportData(res);
      const points: ChartDataPoint[] = res.labels.map((label, i) => ({
        label,
        new_accounts: res.new_accounts[i] ?? 0,
        renewals: res.renewals[i] ?? 0,
      }));

      setChartData(points);
      toast.success('Report generated successfully!');
    } catch {
      toast.error('Failed to generate report');
    } finally {
      setIsLoading(false);
    }
  }, [buildParams]);

  return (
    <div>
      <div className="flex justify-between lg:items-center gap-7.5 lg:flex-row flex-col mb-6">
        <div className="text-xs flex lg:flex-row flex-col gap-x-8 gap-y-3">
          <h1 className="text-[23px]">Eval Account By Month</h1>
        </div>
      </div>

      <Card className="mt-4.5 pb-10">
        <div className="justify-between items-start mb-5 gap-6">
          <div className="w-full flex gap-6 mb-5 items-end">
            <div className="flex flex-col gap-2">
              <label className="text-xs text-[#919EAB]" htmlFor="month-select">
                Select Month
              </label>
              <MonthPicker
                value={selectedMonth}
                onChange={(date) => {
                  setSelectedMonth(date);
                  setUseAllTime(false);
                }}
              />
            </div>
            <div className="flex items-center gap-2 pb-1">
              <input
                checked={useAllTime}
                className="w-4 h-4 accent-[#B6D72C] cursor-pointer"
                id="all-time"
                type="checkbox"
                onChange={(e) => setUseAllTime(e.target.checked)}
              />
              <label
                className="text-sm text-[#919EAB] cursor-pointer"
                htmlFor="all-time"
              >
                All-time totals
              </label>
            </div>
          </div>

          <div className="flex gap-5">
            <Button
              className="bg-[#B6D72C] text-black hover:bg-[#9fb824] px-6"
              disabled={isLoading}
              onClick={handleGetData}
            >
              {isLoading ? 'Loading...' : 'Run Report'}
            </Button>
          </div>
        </div>

        {reportData && (
          <div className="flex gap-8 mt-4 mb-6 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-[#919EAB]">New Total:</span>
              <span className="text-white font-semibold">
                {reportData.new_total}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[#919EAB]">Renewal Total:</span>
              <span className="text-white font-semibold">
                {reportData.renewal_total}
              </span>
            </div>
          </div>
        )}

        <ResponsiveContainer height={770} width="100%">
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
          >
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: 'transparent' }}
            />
            <Legend
              align="center"
              content={<CustomLegend title={reportData?.title} />}
              layout="horizontal"
              verticalAlign="top"
            />
            <CartesianGrid stroke="#919EAB3D" vertical={false} />
            <XAxis
              angle={-45}
              dataKey="label"
              dy={10}
              fontSize={12}
              height={200}
              interval={0}
              padding={{ left: 10, right: 10 }}
              stroke="#919EAB"
              textAnchor="end"
              tickLine={false}
            />
            <YAxis
              fontSize={12}
              label={{
                value: 'No. of Accounts',
                angle: -90,
                position: 'insideLeft',
              }}
              stroke="#919EAB"
              tickLine={false}
              yAxisId="left"
            />
            <Bar
              barSize={19}
              dataKey="renewals"
              fill="#B6D72C"
              stackId="a"
              yAxisId="left"
            />
            <Bar
              barSize={19}
              dataKey="new_accounts"
              fill="#4A90E2"
              stackId="a"
              yAxisId="left"
            />
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
};
